import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Logger } from '@vp/ubik-logging'

interface ErrorBoundaryProps {
  children: ReactNode
  logger?: Logger
  context?: Record<string, any>
  fallback?: ReactNode
  debug?: boolean
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { logger, context = {} } = this.props

    if (logger) {
      logger.error('ErrorBoundary caught an error', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        ...context
      })
    } else {
      // Fallback to console if no logger is provided
      console.error('ErrorBoundary caught an error:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        ...context
      })
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.debug) {
        return (
          <div
            style={{
              padding: '20px',
              border: '1px solid #ff6b6b',
              borderRadius: '4px',
              backgroundColor: '#ffe0e0',
              color: '#d63031'
            }}
          >
            <div>Something went wrong</div>
            <p>An error occurred while rendering this component.</p>
          </div>
        )
      }

      if (this.props.fallback) {
        return this.props.fallback
      }

      return null
    }

    return this.props.children
  }
}

export default ErrorBoundary
